// @flow

import * as React from 'react';

import { ArrowLeft } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { compose } from 'redux';
import { FormControl, Text, TextField, View } from 'reshaped';

import { withPageMeta } from '@gandi/app-runner';
import { needsAuth } from '@gandi/authentication';
import { selectIsFetchingOneOrder } from '@gandi/billing';
import { Button } from '@gandi/reshaped';
import { useTranslator } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { GenerateProformaButton } from '../Layout/components/CartSummary/components/GenerateProformaButon';
import { useCartSummaryContext } from '../Layout/context/CartSummary';
import { selectIsAppWorking } from '../modules/app';

import OrderError from './components/OrderError';
import { selectOrderError } from './modules';

type Params = {
  cartId: string,
};

function GenerateProforma() {
  const params = useParams<Params>();

  const [value, setValue] = React.useState<string>('');

  const { orderId } = useCartSummaryContext();

  const orderError = useAppSelector(
    state => !!orderId && selectOrderError(state, { orderId }),
  );

  const isAppWorking = useAppSelector(selectIsAppWorking);
  const isFetchingOrder = useAppSelector(state =>
    // $FlowFixMe[incompatible-call]
    selectIsFetchingOneOrder(state, { orderId }),
  );

  const loading = isAppWorking || isFetchingOrder;

  const { __ } = useTranslator();

  if (!!orderId && orderError) {
    return <OrderError orderId={orderId} />;
  }

  return (
    <View gap={6}>
      <View gap={1}>
        <Text variant="featured-3" weight="bold" as="h3">
          {__('Your Proforma details')}
        </Text>
        <Text variant="body-2" weight="medium" as="p" color="neutral-faded">
          {__('You can add a purchase order if necessary for your business.')}
        </Text>
      </View>

      <FormControl>
        <FormControl.Label>
          {__('Purchase order (optional):')}
        </FormControl.Label>
        <TextField
          value={value}
          onChange={evt => setValue(evt.value || '')}
          name="external_reference"
          placeholder={__('Type a purchase order if needed')}
        />
      </FormControl>

      <View direction={{ m: 'row' }} gap={3}>
        <View.Item grow>
          <Button
            size="large"
            fullWidth
            type="button"
            icon={ArrowLeft}
            disabled={loading}
            variant="outline"
            to={`/cart/${params.cartId}/overview`}
          >
            {__('Go back')}
          </Button>
        </View.Item>
        <View.Item grow>
          <GenerateProformaButton purchaseOrder={value} />
        </View.Item>
      </View>
    </View>
  );
}

export default (compose(
  withPageMeta(args => ({
    title: args.__('Generate a pro forma invoice - Gandi.net'),
  })),
  needsAuth,
)(GenerateProforma): React.ComponentType<{}>);
